import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { HiMiniArrowLeft, HiMiniEye} from "react-icons/hi2";
import { TbThumbUp, TbThumbDown } from "react-icons/tb";

const FaqDetail = () => {
  const { slug, faqSlug } = useParams();
  const [faqData, setFaqData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [voting, setVoting] = useState(false);
  const [userVote, setUserVote] = useState(null);

  useEffect(() => {
    const fetchFaqData = async () => {
      try {
        setLoading(true);
        
        const categoriesResponse = await fetch('https://api.gmskripto.com/public/api/categories');
        const categoriesData = await categoriesResponse.json();
        
        const category = categoriesData.data.find(cat => cat.slug === slug);
        
        if (!category) {
          throw new Error('Kategori bulunamadı');
        }

        const categoryResponse = await fetch(`https://api.gmskripto.com/public/api/categories/${category.id}`);
        const categoryData = await categoryResponse.json();
        
        const faq = categoryData.data.faqs.find(f => f.slug === faqSlug);
        
        if (!faq) {
          throw new Error('Soru bulunamadı');
        }

        const response = await fetch(`https://api.gmskripto.com/public/api/faqs/${faq.id}`);
        
        if (!response.ok) {
          throw new Error('Soru verileri yüklenirken bir hata oluştu');
        }
        
        const data = await response.json();
        setFaqData(data.data);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching FAQ data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (slug && faqSlug) {
      fetchFaqData();
    }
  }, [slug, faqSlug]);

  const handleVote = async (isHelpful) => {
    if (!faqData || voting) return;

    try {
      setVoting(true);
      
      const response = await fetch(`https://api.gmskripto.com/public/api/faqs/${faqData.id}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_helpful: isHelpful
        })
      });

      if (!response.ok) {
        throw new Error('Oylama gönderilirken bir hata oluştu');
      }

      const result = await response.json();
      
      setFaqData(prev => ({
        ...prev,
        helpful_count: result.faq.helpful_count,
        not_helpful_count: result.faq.not_helpful_count,
        helpfulness_percentage: result.faq.helpfulness_percentage
      }));

      setUserVote(isHelpful ? 'helpful' : 'not_helpful');
      
    } catch (err) {
      console.error('Error submitting vote:', err);
      alert('Oylama gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setVoting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[rgb(24,25,29)] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[rgb(24,25,29)] flex items-center justify-center">
        <div className="text-red-400 text-xl text-center">
          <div>Hata: {error}</div>
          <Link 
            to="/destek-merkezi"
            className="mt-4 inline-block px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Destek Merkezine Dön
          </Link>
        </div>
      </div>
    );
  }

  if (!faqData) {
    return (
      <div className="min-h-screen bg-[rgb(24,25,29)] flex items-center justify-center">
        <div className="text-gray-400 text-xl">Soru bulunamadı</div>
      </div>
    );
  }

  return (
    <div className="bg-[rgb(24,25,29)] min-h-screen">
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-500/20 to-[rgb(24,25,29)]"></div>
        
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div className="absolute top-10 left-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative pt-8 pb-16">
          <div className="container mx-auto px-6">
            <div className="mb-8">
              <div className="flex items-center space-x-2 text-sm text-gray-400 mb-4">
                <Link to="/destek-merkezi" className="hover:text-purple-400 transition-colors">
                  Destek Merkezi
                </Link>
                <span>/</span>
                <Link to={`/destek-merkezi/${faqData.category.slug}`} className="hover:text-purple-400 transition-colors">
                  {faqData.category.name}
                </Link>
                <span>/</span>
                <span className="text-white">{faqData.question}</span>
              </div>
              
              <Link 
                to={`/destek-merkezi/${faqData.category.slug}`}
                className="inline-flex items-center text-purple-400 hover:text-purple-300 transition-colors"
              >
                <HiMiniArrowLeft className="w-5 h-5 mr-2" />
                Kategoriye Dön
              </Link>
            </div>

            <div className="w-full">
              <div className="inline-flex items-center bg-black/80 backdrop-blur-sm rounded-full py-1.5 px-4 mb-4 border border-purple-500/30">
                <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse mr-2"></span>
                <span className="text-purple-400 text-sm font-medium">Soru & Cevap</span>
              </div>
              
              <div className="bg-gradient-to-br from-purple-900/20 to-black/30 border border-purple-800/30 rounded-2xl p-8 mb-8 backdrop-blur-sm">
                <div className="flex items-start space-x-6">
                  <div className="text-purple-400 flex-shrink-0">
                    <div className="w-16 h-16 bg-purple-500/20 rounded-xl flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h1 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">{faqData.question}</h1>
                    <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
                      <div className="flex items-center space-x-2">
                        <HiMiniEye className="w-4 h-4" />
                        <span>{faqData.view_count} görüntülenme</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <TbThumbUp className="w-4 h-4" />
                        <span>{faqData.helpful_count} faydalı</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <TbThumbDown className="w-4 h-4" />
                        <span>{faqData.not_helpful_count} faydalı değil</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 pb-20">
        <div className="w-full max-w-4xl mx-auto">
          <div className="bg-black/20 border border-purple-500 rounded-2xl p-8 mb-8 backdrop-blur-sm">
            <div className="prose prose-invert max-w-none">
              <div 
                className="text-gray-300 leading-relaxed text-lg"
                dangerouslySetInnerHTML={{ __html: faqData.safe_answer }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-purple-900/10 to-black/30 border border-purple-800/30 rounded-xl p-6 backdrop-blur-sm">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-purple-400 font-semibold">Kategori</h3>
              </div>
              <Link 
                to={`/destek-merkezi/${faqData.category.slug}`}
                className="text-white text-lg hover:text-purple-400 transition-colors"
              >
                {faqData.category.name}
              </Link>
            </div>

            <div className="bg-gradient-to-br from-purple-900/10 to-black/30 border border-purple-800/30 rounded-xl p-6 backdrop-blur-sm">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-purple-400 font-semibold">Tarih</h3>
              </div>
              <p className="text-white text-lg">{new Date(faqData.created_at).toLocaleDateString('tr-TR')}</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-900/10 to-black/30 border border-purple-800/30 rounded-2xl p-8 text-center backdrop-blur-sm">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">Bu cevap size yardımcı oldu mu?</h3>
              <p className="text-gray-400">Geri bildiriminiz bizim için değerli</p>
            </div>
            
            <div className="flex justify-center space-x-6">
              <button 
                onClick={() => handleVote(true)}
                disabled={voting || userVote === 'helpful'}
                className={`flex items-center space-x-3 px-8 py-4 rounded-xl transition-all duration-300 ${
                  userVote === 'helpful' 
                    ? 'bg-green-700 text-white cursor-not-allowed shadow-lg' 
                    : 'bg-green-600 hover:bg-green-700 text-white hover:shadow-lg hover:shadow-green-500/30'
                } ${voting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <TbThumbUp className="w-6 h-6" />
                <span className="font-semibold">{voting ? 'Gönderiliyor...' : 'Evet, faydalı'}</span>
              </button>
              
              <button 
                onClick={() => handleVote(false)}
                disabled={voting || userVote === 'not_helpful'}
                className={`flex items-center space-x-3 px-8 py-4 rounded-xl transition-all duration-300 ${
                  userVote === 'not_helpful' 
                    ? 'bg-red-700 text-white cursor-not-allowed shadow-lg' 
                    : 'bg-red-600 hover:bg-red-700 text-white hover:shadow-lg hover:shadow-red-500/30'
                } ${voting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <TbThumbDown className="w-6 h-6" />
                <span className="font-semibold">{voting ? 'Gönderiliyor...' : 'Hayır, faydalı değil'}</span>
              </button>
            </div>
            
            {userVote && (
              <div className="mt-6 p-4 bg-green-500/20 border border-green-500/30 rounded-xl">
                <p className="text-green-400 font-semibold">
                  ✓ Oylamanız başarıyla kaydedildi! Teşekkür ederiz.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaqDetail; 